import { AllowedTenant } from "@albertine/claims";
import type { Tenant } from "albertine-shared-web";
import type { ParsedToken } from "firebase/auth";
import { createContext, type PropsWithChildren, useContext, useState } from "react";
import { AgentClaims, checkAgentClaims } from "../common/types/Claims";
import {
    type AgentScope,
    scopeAllowed,
    tenantAllowed,
} from "../common/types/PermissionGroup";

type ClaimsContextType = (
    | { uid: string; idToken: string }
    | { uid: null; idToken: null }
) & {
    claims: Pick<AgentClaims, "agentScopes" | "tenants">;
    hasScope: (scope: AgentScope) => boolean;
    hasTenant: (tenantId: Tenant) => boolean;
    isAdmin: boolean;
};

const emptyClaimsContext: ClaimsContextType = {
    uid: null,
    idToken: null,
    claims: {
        agentScopes: [],
        tenants: [],
    },
    hasScope: () => false,
    hasTenant: () => false,
    isAdmin: false,
};

const ClaimsContext = createContext<ClaimsContextType>(emptyClaimsContext);

type ClaimsProviderProps = PropsWithChildren<{
    uid: string | null;
    idToken: string | null;
    claims: ParsedToken;
}>;

export const ClaimsProvider: React.FC<ClaimsProviderProps> = ({
    uid,
    idToken,
    claims,
    children,
}) => {
    const claimsParsed = AgentClaims.safeParse(claims);

    if (!uid || !idToken || !claimsParsed.success) {
        console.warn("Failed to parse claims from context.", {
            claims,
            error: claimsParsed.error,
        });

        return (
            <ClaimsContext.Provider value={emptyClaimsContext}>
                {children}
            </ClaimsContext.Provider>
        );
    }

    const agentHasAdminClaim = checkAgentClaims(claimsParsed.data, {
        tenant: null,
        scope: "admin",
    });

    const value = {
        uid,
        idToken,
        claims: claimsParsed.data,
        hasScope: (scope: AgentScope) =>
            scopeAllowed(scope, claimsParsed.data.agentScopes),
        hasTenant: (tenant: Tenant) => tenantAllowed(tenant, claimsParsed.data.tenants),
        isAdmin: agentHasAdminClaim.ok,
    };

    return <ClaimsContext.Provider value={value}>{children}</ClaimsContext.Provider>;
};

export const useClaims = () => {
    return useContext(ClaimsContext);
};
